/**
 * Mobile Team Stats Column Configurations
 *
 * Bible § 4 stat-column rule (ratified 2026-05-12): stat columns are derived
 * from league_settings.scoring_categories via statConfigs (built by
 * utils/statMapping.getStatConfigs). Hardcoded stat-column lists are
 * forbidden — only context columns (G/GS for pitchers, G/AB for batters)
 * and financial columns (price/salary/contract) are declared statically.
 */

import React from 'react';
import { dynastyTheme } from '../../../services/colorService';
import { formatContractDisplay } from '../../../services/contractUtils';
import { formatBattingAvg, formatERA } from '../../../services/statFormatUtils';
import { buildDynamicStatColumn } from '../../../utils/dynamicStatColumn';
import { statFieldSimple } from '../../../utils/statColumnDedup';
import MobilePlayerLink from './MobilePlayerLink';

// =============================================================================
// BATTERS COLUMNS (Accrued Only)
// =============================================================================
export const createMobileBattersColumns = (callbacks = {}) => {
  const {
    onPlayerClick,
    excludePlayerColumn = false,
    excludeTeamColumn = false,
    excludeFinancialColumns = false,
    statConfigs = [],
  } = callbacks;
  
  const columns = [
    // Position Slot (Leftmost - NEW!)
    {
      key: 'position_slot',
      header: 'POS',
      width: '32px',
      minWidth: '32px',
      maxWidth: '32px',
      textAlign: 'center',
      sticky: true,
      fontSize: '9px',
      accessor: (row) => {
        if (row.isTotalsRow) return null;
        return row.slotPosition;
      },
      render: (value, row) => {
        // Totals row - show "Totals"
        if (row.isTotalsRow) {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.white,
              fontWeight: '400'
            }}>
              Totals
            </span>
          );
        }
        // 3-line mode: Only show position on rolling row (row 2) - YELLOW
        if (row.statType === 'rolling') {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.warning,
              fontWeight: '400'
            }}>
              {value}
            </span>
          );
        }
        // 1-line mode: Show position in WHITE (either no statType OR accrued with rowsPerItem === 1)
        if (!row.statType || (row.statType === 'accrued' && row.rowsPerItem === 1)) {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.white,
              fontWeight: '400'
            }}>
              {value}
            </span>
          );
        }
        // Season row (row 1) and Accrued row (row 3) in 3-line mode: Empty
        return null;
      }
    },
    
    // Player Name (Sticky, Clickable)
    {
      key: 'player_name',
      header: 'Player',
      width: '110px',
      minWidth: '110px',
      maxWidth: '110px',
      textAlign: 'left',
      sticky: true,
      nowrap: true,
      ellipsis: true,
      accessor: (row) => {
        if (row.isTotalsRow || !row.player?.info) return null;
        return row.player.info.full_name;
      },
      render: (value, row) => {
        // Rolling row (row 2 in 3-line mode) - show "14-Day" label in YELLOW
        if (row.statType === 'rolling') {
          return (
            <span style={{ 
              fontSize: '10px',
              color: dynastyTheme.tokens.colors.warning 
            }}>
              14-Day
            </span>
          );
        }
        
        // Accrued row: Show "Accrued" label only in 3-line mode (rowsPerItem === 3)
        // In 1-line mode (rowsPerItem === 1), show player name instead
        if (row.statType === 'accrued') {
          // If rowsPerItem is 3, we're in 3-line mode → show "Accrued" label
          if (row.rowsPerItem === 3) {
            return (
              <span style={{ 
                fontSize: '10px',
                color: dynastyTheme.tokens.colors.success 
              }}>
                Accrued
              </span>
            );
          }
          // If rowsPerItem is 1, we're in 1-line mode → show player name (fall through)
        }
        
        // Show player name if we have a value (for season row and 1-line accrued-only mode)
        if (value && row) {
          const mlbId = row.player?.ids?.mlb;
          return (
            <MobilePlayerLink
              mlbId={mlbId}
              name={value}
              onClick={onPlayerClick ? (id) => onPlayerClick(id) : null}
              style={{ fontWeight: 400, fontSize: 'inherit' }}
            />
          );
        }

        return value || null;
      }
    },

    // Team
    {
      key: 'team',
      header: 'Tm',
      width: '32px',
      textAlign: 'center',
      fontSize: '9px',
      accessor: (row) => {
        if (row.isTotalsRow || !row.player?.info) return null;
        return row.player.info.mlb_team;
      },
      render: (value, row) => {
        // Totals row - hide team column
        if (row.isTotalsRow) {
          return null;
        }
        // 3-line mode: Only show team on rolling row (row 2) - WHITE
        if (row.statType === 'rolling') {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.white
            }}>
              {value}
            </span>
          );
        }
        // 1-line mode: Show team in WHITE (either no statType OR accrued with rowsPerItem === 1)
        if (!row.statType || (row.statType === 'accrued' && row.rowsPerItem === 1)) {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.white
            }}>
              {value}
            </span>
          );
        }
        // Season row (row 1) and Accrued row (row 3) in 3-line mode: Empty
        return null;
      }
    },
    
    // Games
    {
      key: 'games',
      header: 'G',
      width: '24px',
      accessor: (row) => {
        if (row.isTotalsRow) {
          return row.totals.games_played;
        }
        if (row.statType === 'season') {
          return row.player?.stats?.season?.hitting?.games_played;
        }
        if (row.statType === 'rolling') {
          return row.player?.stats?.rolling_14_day?.hitting?.games_played;
        }
        // accrued (default)
        return row.player?.stats?.active_accrued?.hitting?.games_played;
      },
    },
    
    // At Bats
    {
      key: 'ab',
      header: 'AB',
      width: '28px',
      accessor: (row) => {
        if (row.isTotalsRow) {
          return row.totals.at_bats;
        }
        if (row.statType === 'season') {
          return row.player?.stats?.season?.hitting?.at_bats;
        }
        if (row.statType === 'rolling') {
          return row.player?.stats?.rolling_14_day?.hitting?.at_bats;
        }
        return row.player?.stats?.active_accrued?.hitting?.at_bats;
      },
    },
    
    // === Dynamic scoring-category columns (driven by league_settings.scoring_categories) ===
    // DD-STAT-CONSOLIDATION sweep: exclude dynamic G/AB — static columns render them.
    ...statConfigs
      .filter((c) => c.category === 'hitting' && !['games_played', 'at_bats'].includes(statFieldSimple(c)))
      .map(buildDynamicStatColumn),

    // Price
    {
      key: 'price',
      header: 'Price',
      width: '40px',
      textAlign: 'center',
      fontSize: '10px',
      color: dynastyTheme.tokens.colors.warning,
      accessor: (row) => {
        const player = row.player || row;
        return player.league_context?.financial?.baseline_price;
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          const val = parseFloat(value) || 0;
          return (
            <span style={{ color: dynastyTheme.tokens.colors.warning }}>
              {val > 0 ? `${val}` : '-'}
            </span>
          );
        }
        // 1-line mode: Show price (either no statType OR accrued with rowsPerItem === 1) - YELLOW
        if (!row.statType || (row.statType === 'accrued' && row.rowsPerItem === 1)) {
          const val = parseFloat(value) || 0;
          return (
            <span style={{ color: dynastyTheme.tokens.colors.warning }}>
              {val > 0 ? `${val}` : '-'}
            </span>
          );
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      },
    },
    
    // Salary
    {
      key: 'salary',
      header: 'Salary',
      width: '40px',
      textAlign: 'center',
      fontSize: '10px',
      color: dynastyTheme.tokens.colors.success,
      accessor: (row) => {
        const player = row.player || row;
        return player.league_context?.financial?.contract_salary;
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          const val = parseFloat(value) || 0;
          return (
            <span style={{ color: dynastyTheme.tokens.colors.success }}>
              {val > 0 ? `${val}` : '-'}
            </span>
          );
        }
        // 1-line mode: Show salary (either no statType OR accrued with rowsPerItem === 1)
        if (!row.statType || (row.statType === 'accrued' && row.rowsPerItem === 1)) {
          const val = parseFloat(value) || 0;
          return val > 0 ? `${val}` : '-';
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      },
    },
    
    // Contract
    {
      key: 'contract',
      header: 'Contract',
      width: '50px',
      textAlign: 'center',
      fontSize: '10px',
      color: dynastyTheme.tokens.colors.neutral[400],
      accessor: (row) => {
        const player = row.player || row;
        return formatContractDisplay(player.league_context?.financial?.contract_type, player.league_context?.financial?.contract_years);
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          return (
            <span style={{ color: value === '\u2014' ? dynastyTheme.tokens.colors.neutral[600] : dynastyTheme.tokens.colors.white }}>
              {value}
            </span>
          );
        }
        // 1-line mode: Show contract (either no statType OR accrued with rowsPerItem === 1) - WHITE
        if (!row.statType || (row.statType === 'accrued' && row.rowsPerItem === 1)) {
          return (
            <span style={{ color: value === '\u2014' ? dynastyTheme.tokens.colors.neutral[600] : dynastyTheme.tokens.colors.white }}>
              {value}
            </span>
          );
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      },
    }
  ];
  
  // Remove player column if excludePlayerColumn is true (for Team Totals)
  let filteredColumns = columns;
  if (excludePlayerColumn) {
    filteredColumns = filteredColumns.filter(col => col.key !== 'player_name');
  }
  if (excludeTeamColumn) {
    filteredColumns = filteredColumns.filter(col => col.key !== 'team');
  }
  if (excludeFinancialColumns) {
    filteredColumns = filteredColumns.filter(col => !['price', 'salary', 'contract'].includes(col.key));
  }
  
  return filteredColumns;
};

// =============================================================================
// PITCHERS COLUMNS (Accrued Only)
// =============================================================================
export const createMobilePitchersColumns = (callbacks = {}) => {
  const {
    onPlayerClick,
    excludePlayerColumn = false,
    excludeTeamColumn = false,
    excludeFinancialColumns = false,
    statConfigs = [],
  } = callbacks;
  
  const columns = [
    // Position Slot (Leftmost - NEW!)
    {
      key: 'position_slot',
      header: 'POS',
      width: '32px',
      minWidth: '32px',
      maxWidth: '32px',
      textAlign: 'center',
      sticky: true,
      fontSize: '9px',
      accessor: (row) => {
        if (row.isTotalsRow) return null;
        return row.slotPosition;
      },
      render: (value, row) => {
        // Totals row - show "Totals"
        if (row.isTotalsRow) {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.white,
              fontWeight: '400'
            }}>
              Totals
            </span>
          );
        }
        // 3-line mode: Only show position on rolling row (row 2) - YELLOW
        if (row.statType === 'rolling') {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.warning,
              fontWeight: '400'
            }}>
              {value}
            </span>
          );
        }
        // 1-line mode: Show position in WHITE (either no statType OR accrued with rowsPerItem === 1)
        if (!row.statType || (row.statType === 'accrued' && row.rowsPerItem === 1)) {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.white,
              fontWeight: '400'
            }}>
              {value}
            </span>
          );
        }
        // Season row (row 1) and Accrued row (row 3) in 3-line mode: Empty
        return null;
      }
    },
    
    // Player Name (Sticky, Clickable)
    {
      key: 'player_name',
      header: 'Player',
      width: '110px',
      minWidth: '110px',
      maxWidth: '110px',
      textAlign: 'left',
      sticky: true,
      nowrap: true,
      ellipsis: true,
      accessor: (row) => {
        if (row.isTotalsRow || !row.player?.info) return null;
        return row.player.info.full_name;
      },
      render: (value, row) => {
        // Rolling row (row 2 in 3-line mode) - show "14-Day" label in YELLOW
        if (row.statType === 'rolling') {
          return (
            <span style={{ 
              fontSize: '10px',
              color: dynastyTheme.tokens.colors.warning 
            }}>
              14-Day
            </span>
          );
        }
        
        // Accrued row: Show "Accrued" label only in 3-line mode (rowsPerItem === 3)
        // In 1-line mode (rowsPerItem === 1), show player name instead
        if (row.statType === 'accrued') {
          // If rowsPerItem is 3, we're in 3-line mode → show "Accrued" label
          if (row.rowsPerItem === 3) {
            return (
              <span style={{ 
                fontSize: '10px',
                color: dynastyTheme.tokens.colors.success 
              }}>
                Accrued
              </span>
            );
          }
          // If rowsPerItem is 1, we're in 1-line mode → show player name (fall through)
        }
        
        // Show player name if we have a value (for season row and 1-line accrued-only mode)
        if (value && row) {
          const mlbId = row.player?.ids?.mlb;
          return (
            <MobilePlayerLink
              mlbId={mlbId}
              name={value}
              onClick={onPlayerClick ? (id) => onPlayerClick(id) : null}
              style={{ fontWeight: 400, fontSize: 'inherit' }}
            />
          );
        }

        return value || null;
      }
    },

    // Team
    {
      key: 'team',
      header: 'Tm',
      width: '32px',
      textAlign: 'center',
      fontSize: '9px',
      accessor: (row) => {
        if (row.isTotalsRow || !row.player?.info) return null;
        return row.player.info.mlb_team;
      },
      render: (value, row) => {
        // Totals row - hide team column
        if (row.isTotalsRow) {
          return null;
        }
        // 3-line mode: Only show team on rolling row (row 2) - WHITE
        if (row.statType === 'rolling') {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.white
            }}>
              {value}
            </span>
          );
        }
        // 1-line mode: Show team in WHITE (either no statType OR accrued with rowsPerItem === 1)
        if (!row.statType || (row.statType === 'accrued' && row.rowsPerItem === 1)) {
          return (
            <span style={{
              color: dynastyTheme.tokens.colors.white
            }}>
              {value}
            </span>
          );
        }
        // Season row (row 1) and Accrued row (row 3) in 3-line mode: Empty
        return null;
      }
    },
    
    // Games
    {
      key: 'games',
      header: 'G',
      width: '24px',
      accessor: (row) => {
        if (row.isTotalsRow) {
          return row.totals.games_played;
        }
        if (row.statType === 'season') {
          return row.player?.stats?.season?.pitching?.games_played || 0;
        }
        if (row.statType === 'rolling') {
          return row.player?.stats?.rolling_14_day?.pitching?.games_played || 0;
        }
        return row.player?.stats?.active_accrued?.pitching?.games_played || 0;
      },
    },
    
    // Games Started (NEW!)
    {
      key: 'gs',
      header: 'GS',
      width: '24px',
      accessor: (row) => {
        if (row.isTotalsRow) {
          return row.totals.games_started;
        }
        if (row.statType === 'season') {
          return row.player?.stats?.season?.pitching?.games_started || 0;
        }
        if (row.statType === 'rolling') {
          return row.player?.stats?.rolling_14_day?.pitching?.games_started || 0;
        }
        return row.player?.stats?.active_accrued?.pitching?.games_started || 0;
      },
    },
    
    // === Dynamic scoring-category columns (driven by league_settings.scoring_categories) ===
    // DD-STAT-CONSOLIDATION sweep: exclude dynamic G/GS — static columns render them.
    ...statConfigs
      .filter((c) => c.category === 'pitching' && !['games_played', 'games_started'].includes(statFieldSimple(c)))
      .map(buildDynamicStatColumn),

    // Price
    {
      key: 'price',
      header: 'Price',
      width: '40px',
      textAlign: 'center',
      fontSize: '10px',
      color: dynastyTheme.tokens.colors.warning,
      accessor: (row) => {
        const player = row.player || row;
        return player.league_context?.financial?.baseline_price;
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          const val = parseFloat(value) || 0;
          return (
            <span style={{ color: dynastyTheme.tokens.colors.warning }}>
              {val > 0 ? `${val}` : '-'}
            </span>
          );
        }
        // 1-line mode: Show price (either no statType OR accrued with rowsPerItem === 1) - YELLOW
        if (!row.statType || (row.statType === 'accrued' && row.rowsPerItem === 1)) {
          const val = parseFloat(value) || 0;
          return (
            <span style={{ color: dynastyTheme.tokens.colors.warning }}>
              {val > 0 ? `${val}` : '-'}
            </span>
          );
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      },
    },
    
    // Salary
    {
      key: 'salary',
      header: 'Salary',
      width: '40px',
      textAlign: 'center',
      fontSize: '10px',
      color: dynastyTheme.tokens.colors.success,
      accessor: (row) => {
        const player = row.player || row;
        return player.league_context?.financial?.contract_salary;
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          const val = parseFloat(value) || 0;
          return (
            <span style={{ color: dynastyTheme.tokens.colors.success }}>
              {val > 0 ? `${val}` : '-'}
            </span>
          );
        }
        // 1-line mode: Show salary (either no statType OR accrued with rowsPerItem === 1)
        if (!row.statType || (row.statType === 'accrued' && row.rowsPerItem === 1)) {
          const val = parseFloat(value) || 0;
          return val > 0 ? `${val}` : '-';
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      },
    },
    
    // Contract
    {
      key: 'contract',
      header: 'Contract',
      width: '50px',
      textAlign: 'center',
      fontSize: '10px',
      color: dynastyTheme.tokens.colors.neutral[400],
      accessor: (row) => {
        const player = row.player || row;
        return formatContractDisplay(player.league_context?.financial?.contract_type, player.league_context?.financial?.contract_years);
      },
      render: (value, row) => {
        // 3-line mode: Only show on rolling row (row 2)
        if (row.statType === 'rolling') {
          return (
            <span style={{ color: value === '\u2014' ? dynastyTheme.tokens.colors.neutral[600] : dynastyTheme.tokens.colors.white }}>
              {value}
            </span>
          );
        }
        // 1-line mode: Show contract (either no statType OR accrued with rowsPerItem === 1) - WHITE
        if (!row.statType || (row.statType === 'accrued' && row.rowsPerItem === 1)) {
          return (
            <span style={{ color: value === '\u2014' ? dynastyTheme.tokens.colors.neutral[600] : dynastyTheme.tokens.colors.white }}>
              {value}
            </span>
          );
        }
        // Season and Accrued rows in 3-line mode: Empty
        return null;
      },
    }
  ];
  
  // Remove player column if excludePlayerColumn is true (for Team Totals)
  let filteredColumns = columns;
  if (excludePlayerColumn) {
    filteredColumns = filteredColumns.filter(col => col.key !== 'player_name');
  }
  if (excludeTeamColumn) {
    filteredColumns = filteredColumns.filter(col => col.key !== 'team');
  }
  if (excludeFinancialColumns) {
    filteredColumns = filteredColumns.filter(col => !['price', 'salary', 'contract'].includes(col.key));
  }
  
  return filteredColumns;
};
